// ---------------------------------------------------------------------------
// English dictionary — has to translate EXACTLY the same keys as es.ts
// (source of truth for `TextKey`). The `Record<TextKey, string>` type makes
// a missing key a TypeScript error, not a silent blank text in production.
// ---------------------------------------------------------------------------
import type { TextKey } from "./es.ts";

export const en: Record<TextKey, string> = {
  // --- Common (shared across components) ---
  "common.close": "Close",
  "common.date": "Date",
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.emptyName": "The name can't be empty.",
  "common.language": "Language",
  "common.apply": "Apply",
  "common.choose": "— select —",
  "common.name": "Name",
  "common.create": "Create",
  "common.unassigned": "— unassigned —",
  "common.unassignedMissingZone": "unassigned (missing zone)",
  "common.series": "Series",
  "common.back": "Back",
  "common.next": "Next",
  "common.finish": "Finish",
  "common.add": "Add",

  // --- TopBar.tsx ---
  "topbar.closeProject": "Close project",
  "topbar.moreOptions": "More project options",
  "topbar.exampleUnsavedWarning":
    'This is an example, not a saved project — use "Save copy as…" first.',
  "topbar.renameProject": "Rename project…",
  "topbar.saveCopyAsButton": "Save copy as…",
  "topbar.exportCopy": "Export copy…",
  "topbar.updateFromCsv": "Update from CSVs…",
  "topbar.deleteThisProject": "Delete this project",
  "topbar.saveAsDialogTitle": "Save copy as",
  "topbar.copyNameSuffix": "{name} (copy)",
  "topbar.renameDialogTitle": "Rename project",
  "topbar.deleteDialogTitle": "Delete project",
  "topbar.deleteProjectMessage":
    'This will permanently delete "{name}" from this browser — this can\'t be undone. If you need to, you can export a copy before deleting it.',
  "topbar.help": "Help: what you can do",
  "topbar.projectInfo": "Project information",
  "topbar.notices": "Notices",
  "topbar.changeView": "Switch view",
  "topbar.viewAsCards": "View as cards (no map)",
  "topbar.viewOnMap": "View on the map",
  "topbar.editProject": "Edit project",
  "topbar.calibration": "Parameter calibration",
  "topbar.calculate": "Calculate",
  "topbar.calculating": "Calculating... {n}/{total}",
  "topbar.cannotCalculate": "Can't calculate: {reasons}",
  "topbar.calculateTitle": "Choose dates and aquifer options, and calculate",
  "topbar.recalculate": "Recalculate",
  "topbar.recalculateTitle": "Recalculate with the same options (without opening the dialog)",
  "topbar.results": "Results",
  "topbar.subBasins": "sub-basins",
  "topbar.searchSubBasin": "Find sub-basin",
  "topbar.searchSubBasinPlaceholder": "Sub-basin name…",
  "topbar.searchSubBasinNoMatches": "No matches",
  "topbar.aemetDownloads": "AEMET downloads",
  "topbar.downloadInProgressWarningTitle": "There are AEMET downloads in progress",
  "topbar.downloadInProgressWarningMessage":
    "If you close the project now, the AEMET download(s) still running will be interrupted and won't be applied. Close anyway?",
  "topbar.closeAnyway": "Close anyway",

  // --- ConfirmDeleteDialog.tsx ---
  "confirmDelete.deletePermanently": "Delete permanently",
  "confirmDelete.defaultMessage":
    "This will permanently delete \"{name}\" — this can't be undone. If it came from a GIS layer, you can recover its basic geometry by re-importing or redrawing it, but you'll have to reconfigure it (zone, topology, settings...) from scratch.",

  // --- ExportChoiceDialog.tsx ---
  "exportChoice.title": "Export copy",
  "exportChoice.ehdzTitle": "Portable copy (.ehdz)",
  "exportChoice.ehdzDescription":
    "A backup of this project exactly as it is in this app — to move it to another browser/machine or keep it aside. Only this app can open it again.",
  "exportChoice.csvTitle": "CSV files (.zip)",
  "exportChoice.csvDescription":
    "The same files the desktop app writes for its calculation engine (sub-basins, zones, parameters, aquifers...), zipped together — to inspect them, use them with another tool, or with the desktop EvalHid. Includes a report of any incomplete data found.",
  "exportChoice.generating": "Generating the file…",
  "exportChoice.error": "Could not generate the file: {error}",

  // --- SettingsMenu.tsx ---
  "themeToggle.toDarkMode": "Switch to dark mode",
  "themeToggle.toLightMode": "Switch to light mode",
  "settings.title": "Settings",
  "settings.driveNeedsReconnectTitle": "Settings — Google Drive needs reconnecting",
  "settings.theme": "Theme",
  "settings.version": "Version",
  "settings.drive": "Google Drive",
  "settings.driveConnect": "Connect",
  "settings.driveConnecting": "Connecting…",
  "settings.driveDisconnect": "Disconnect",

  // --- driveSyncStore.ts / UploadExistingProjectsDialog.tsx ---
  "driveSync.notConfigured": "Google Drive isn't configured for this deployment.",
  "driveSync.signInTimeout": "Timed out waiting for Google sign-in. Try again.",
  "driveSync.noAccessToken": "Google didn't return access to Drive. Try again.",
  "driveSync.uploadExistingTitle": "Sync existing projects to Drive?",
  "driveSync.unsyncedCount": "Found 1 project saved on this computer that isn't in Drive yet.",
  "driveSync.unsyncedCountPlural":
    "Found {n} projects saved on this computer that aren't in Drive yet.",
  "driveSync.uploadAll": "Upload all",
  "driveSync.notNow": "Not now",
  "driveSync.statusSynced": "Synced with Google Drive",
  "driveSync.statusSyncing": "Syncing with Google Drive…",
  "driveSync.statusError": "Could not sync with Google Drive",
  "driveSync.useDriveVersion": "Use the Drive version",
  "driveSync.conflictTooltip":
    "Google Drive has a newer version of this project — click to use it (this overwrites the local copy).",

  // --- InfoPopover.tsx ---
  "infoPopover.title": "Loaded project",
  "infoPopover.subBasins": "Sub-basins",
  "infoPopover.aquifers": "Aquifers",
  "infoPopover.outletPoints": "Outlet points",
  "infoPopover.models": "Rainfall-runoff models",
  "infoPopover.precipitation": "Precipitation",
  "infoPopover.etp": "PET",
  "infoPopover.temperature": "Temperature",
  "infoPopover.pumping": "Pumping",
  "infoPopover.notAvailable": "not available",
  "infoPopover.notProvidedOptionalSingular": "not provided (optional)",
  "infoPopover.notProvidedOptionalPlural": "not provided (optional)",
  "infoPopover.dateRange": "{from} to {to} ({n} rows)",

  // --- NoticesPopover.tsx ---
  "noticesPopover.noWarnings": "No notices.",

  // --- ErrorBoundary.tsx ---
  "errorBoundary.title": "Something went wrong",
  "errorBoundary.message":
    "The application ran into an unexpected error and can't continue in this state. You need to reload — your project is saved automatically, so you'll find it in \"Your projects\" when you reopen the app.",
  "errorBoundary.reload": "Reload",

  // --- ExampleBanner.tsx ---
  "exampleBanner.warning":
    "You're viewing an example — changes aren't saved unless you make a copy.",

  // --- MitecoAttributionBanner.tsx ---
  "mitecoAttributionBanner.warning":
    "The {kind} shown come from Spain's Ministry for the Ecological Transition and the Demographic Challenge (MITECO) — free to use, citing MITECO as the author and owner of the information.",
  "mitecoAttributionBanner.basinsType": "official river basins",
  "mitecoAttributionBanner.subBasinsType": "official sub-basins",
  "mitecoAttributionBanner.viewSource": "View source ↗",

  // --- MitecoAemetBanner.tsx ---
  "mitecoAemetBanner.warning":
    "AEMET's ROCIO grid precipitation, temperature and ETP series (1951-2022) are being downloaded automatically in the background for the new sub-basins. You can keep configuring outlet points and zones in the meantime.",

  // --- Landing.tsx ---
  "landing.tagline": "Evaluate basin contributions and aquifer recharge, right in your browser.",
  "landing.yourProjects": "Your projects",
  "landing.driveNotConnectedHint":
    "These projects are only saved on this computer. Connect Google Drive from Settings to also have them on other computers.",
  "landing.openProject": 'Open "{name}"',
  "landing.renameAria": 'Rename "{name}"',
  "landing.rename": "Rename",
  "landing.loading": "Loading…",
  "landing.openFromFolder": "Open project from folder…",
  "landing.createNew": "Create new project…",
  "landing.importCopy": "Import copy…",
  "landing.orTryAnExample": "Or try an example",

  // --- examples.ts ---
  "examples.aguedaGis.title": "With GIS",
  "examples.aguedaGis.description":
    "14 real sub-basins from the Águeda river basin, with a shapefile linked to the map.",
  "examples.aguedaSinGis.title": "Without GIS",
  "examples.aguedaSinGis.description":
    "The same project, without a map layer: sub-basins shown as cards grouped by zone.",

  // --- ChooseEhdDialog.tsx ---
  "chooseEhdDialog.title": "Multiple projects in this folder",
  "chooseEhdDialog.message":
    "This folder has more than one .ehd file (subfolders were searched too) — choose which one to open:",

  // --- CalculationOptionsDialog.tsx ---
  "calculationOptions.title": "Calculation options",
  "calculationOptions.startDate": "Start date",
  "calculationOptions.availableRange":
    "Available range: {min} — {max} (based on the loaded precipitation).",
  "calculationOptions.endDate": "End date",
  "calculationOptions.negativeAquifers": "Negative aquifer values",
  "calculationOptions.modelPumping": "Model pumping",
  "calculationOptions.errorNoDates": "There are no dates in the loaded precipitation series.",
  "calculationOptions.errorStartDateOutOfRange":
    "The start date must be between {min} and {max} (range of the loaded precipitation).",
  "calculationOptions.errorEndDateOutOfRange": "The end date must be between {min} and {max}.",
  "calculationOptions.errorEndBeforeStart": "The end date can't be earlier than the start date.",

  // --- Legend.tsx ---
  "legend.normalSubBasin": "Normal sub-basin",
  "legend.subBasinWithSnow": "Sub-basin with snow",
  "legend.subBasinWithIssues": "Sub-basin with issues",
  "legend.unlinked": "Not linked to the model",
  "legend.outletPoint": "Outlet point",
  "legend.aquifer": "Aquifer",
  "legend.title": "Legend",
  "legend.show": "Show map legend",
  "legend.hide": "Hide legend",

  // --- AssignmentColorLegend.tsx ---
  "assignmentLegend.unassignedLabel": "Unassigned",

  // --- basemap.ts (LayersControl, SubBasinMap.tsx) ---
  "basemap.streets": "Streets",
  "basemap.satellite": "Aerial view",
  "basemap.terrain": "Terrain",

  // --- MapDrawing.tsx ---
  "drawing.subBasin": "sub-basin",
  "drawing.aquifer": "aquifer",
  "drawing.outletPoint": "outlet point",

  // --- SubBasinMap.tsx ---
  "map.noName": "No name",
  "map.area": "Area: {km2} km²",
  "map.zone": "Zone: {zone} ({model})",
  "map.snowZone": "Snow zone: {zone} ({model})",
  "map.outletColon": "Outlet point: {name}",
  "map.aquiferColon": "Aquifer: {name}",
  "map.clickToPlace": "Click on the map to place the {tool}.",
  "map.vertexSingular": "vertex",
  "map.vertexPlural": "vertices",
  "map.vertexCount": '{n} {word} — click to add more, "Finish" with at least 3.',
  "map.finish": "Finish",
  "map.addAsSubBasin": 'Add "{name}" as a sub-basin',

  // --- SubBasinCanvas.tsx ---
  "canvas.noGisLayer":
    'No GIS layer linked — sub-basins grouped by zone, without real geometry. You can link a shapefile from "Edit project".',
  "canvas.noProjectSubBasins": "This project doesn't have any sub-basins yet.",
  "canvas.snowZonesTab": "Snow zones",
  "canvas.noZoneAssigned": "No zone assigned",
  "canvas.noSnowZoneAssigned": "No snow zone assigned",
  "canvas.noOutletAssigned": "No outlet point assigned",
  "canvas.noAquiferAssigned": "No aquifer assigned",
  "canvas.noSubBasins": "No sub-basins",
  "canvas.editEntity": "Edit",
  "canvas.collapseGroup": "Collapse",
  "canvas.expandGroup": "Expand",
  "canvas.multiSelectHelp":
    "Hold Shift and drag over several sub-basins to select them (Ctrl+drag removes), then drag them together onto a zone, an outlet point, or an aquifer.",
  "canvas.oneSelected":
    "{n} sub-basin selected — drag it onto a zone, an outlet point, or an aquifer.",
  "canvas.multipleSelected":
    "{n} sub-basins selected — drag them onto a zone, an outlet point, or an aquifer.",

  // --- comunes (más) ---
  "common.downloadCsv": "Download CSV",

  // --- DetailChart.tsx ---
  "detailChart.title": "{description} — internal detail (partial results)",

  // --- ContributionChart.tsx / AggregatedContributionChart.tsx ---
  "contributionChart.title": "{title} — contribution by {period} ({unit})",

  // --- PasteDailySeries.tsx ---
  "pasteSeries.noDataYet": "— no data yet",
  "pasteSeries.rangeDaily": "{from} to {to} ({n} days)",
  "pasteSeries.rangeMonthly": "{from} to {to} ({n} months)",
  "pasteSeries.formatHint": "Format: {format}",
  "pasteSeries.formatHintUnknownScale":
    "The project's scale isn't set yet — accepted formats: {formats}. It's detected automatically from the first 2 dates.",
  "pasteSeries.noDataSuffix": ", {n} missing",
  "pasteSeries.gapsSuffix": ", {n} date gaps",
  "pasteSeries.average": "Mean: {mean} · Max: {max} · Min: {min}",
  "pasteSeries.replaceWithAnother": "Replace with another series…",
  "pasteSeries.pasteSeries": "Paste series…",
  "pasteSeries.copy": "Copy",
  "pasteSeries.copied": "Copied!",
  "pasteSeries.deleteSeries": "Delete series",
  "pasteSeries.chartView": "View as chart",
  "pasteSeries.tableView": "View as table (edit values)",
  "pasteSeries.value": "Value",
  "pasteSeries.noValue": "no data",
  "pasteSeries.goToDate": "Go to date",
  "pasteSeries.confirmDeleteTitle": "Delete this series?",
  "pasteSeries.confirmDeleteText":
    'The series for "{name}" will be permanently deleted. This can\'t be undone.',
  "pasteSeries.confirmUnlinkText":
    'This data column is shared with another sub-basin or aquifer (possible in older, index-linked projects) — only "{name}" will be unlinked, the data won\'t be deleted.',

  // --- ResultsPanel.tsx ---
  "resultsPanel.close": "Close results",

  // --- CalculationResults.tsx ---
  "units.title": "Units",
  "units.km2": "Km² (× 0.001)",
  "units.ha": "ha (× 0.00001)",
  "units.other": "Other...",
  "units.namePlaceholder": "Name (e.g. Hm³)",
  "units.coefficientPlaceholder": "Coefficient",
  "resultsPanel.totalPerPeriod": "Total ({unit}/{period})",
  "resultsPanel.granularityTitle": "View by",
  "resultsPanel.granularityDaily": "Daily",
  "resultsPanel.granularityMonthly": "Monthly",
  "resultsPanel.granularityAnnual": "Annual",
  "resultsPanel.periodDay": "day",
  "resultsPanel.periodMonth": "month",
  "resultsPanel.periodYear": "year",
  "resultsPanel.surface": "Surface",
  "resultsPanel.groundwater": "Groundwater",
  "resultsPanel.noBreakdown": "Not broken down",
  "resultsPanel.zone": "Zone",
  "resultsPanel.subBasin": "Sub-basin",
  "resultsPanel.calculatedCount": "{n} of {total} sub-basins calculated.",
  "resultsPanel.noColumnWarning":
    "Sub-basins that weren't calculated are missing precipitation or ETP input data.",
  "resultsPanel.aquiferRecharge": "Recharge",
  "resultsPanel.aquiferPumping": "Pumping",
  "resultsPanel.aquiferDischarge": "Aquifer discharge",
  "resultsPanel.oneAquifer": "an aquifer",
  "resultsPanel.alsoCanSelect": "You can also select {what} by clicking on the map.",
  "resultsPanel.oneSubBasin": "a sub-basin",
  "resultsPanel.oneOutletPoint": "an outlet point",
  "resultsPanel.areaKm2": "Area (km²)",
  "resultsPanel.mmPerDay": "mm/day",
  "resultsPanel.mmPerMonth": "mm/month",
  "resultsPanel.unitPerPeriod": "{unit}/{period}",
  "resultsPanel.hideDetail": "Hide detail",
  "resultsPanel.viewDetail": "View detail",

  // --- EditBar.tsx ---
  "panels.subBasins": "Sub-basins",
  "panels.zones": "Zones",
  "panels.inputSeries": "Input series",
  "panels.layers": "Auxiliary layers",
  "editBar.closeEdit": "Close editing",

  // --- SubBasinsPanel.tsx ---
  "subBasinsPanel.createManualTitle": "Create manually",
  "subBasinsPanel.drawTitle": "Draw",
  "subBasinsPanel.csvTitle": "CSV",
  "subBasinsPanel.linkGisLayer": "Link GIS layer",
  "subBasinsPanel.catalog": "All sub-basins",
  "subBasinsPanel.exportCsv": "Export to CSV",
  "subBasinsPanel.oneSelected": "{n} sub-basin selected",
  "subBasinsPanel.multipleSelected": "{n} sub-basins selected",
  "subBasinsPanel.clearSelection": "Clear selection",
  "subBasinsPanel.snowZone": "Snow zone",
  "subBasinsPanel.deleteSelected": "Delete selected",
  "subBasinsPanel.addManual": "Add sub-basin manually",
  "subBasinsPanel.noGis": " (no GIS)",
  "subBasinsPanel.editTitle": "Edit sub-basin",
  "subBasinsPanel.deletePermanentTitle": "Delete sub-basin (permanent)",
  "subBasinsPanel.deleteDialogTitle": "Delete sub-basin",
  "subBasinsPanel.deleteDialogPluralTitle": "Delete sub-basins",
  "subBasinsPanel.addFromMiteco": "Add from MITECO",
  "subBasinsPanel.mitecoBasinsLayer": "Basins",
  "subBasinsPanel.mitecoSubBasinsLayer": "Sub-basins",
  "subBasinsPanel.mitecoHelp":
    "Click to (de)select a polygon, or drag a rectangle (Shift adds, Ctrl removes) for several at once.",
  "subBasinsPanel.mitecoOneSelected": "{n} from MITECO selected",
  "subBasinsPanel.mitecoMultipleSelected": "{n} from MITECO selected",
  "subBasinsPanel.addMitecoSelection": "Add as sub-basins",
  "subBasinsPanel.mitecoAlreadyAdded": "Already added to this project",

  // --- GisLayerPanel.tsx ---
  "gisLayerPanel.layerAlreadyExists":
    "There's already a loaded layer — you can replace it by uploading another one.",
  "gisLayerPanel.uploadShapefile": "Upload the sub-basin shapefile or a GeoJSON.",
  "gisLayerPanel.modeLink": "Link",
  "gisLayerPanel.modeRecover": "Recover deleted",
  "gisLayerPanel.linkColumn": "Link column",
  "gisLayerPanel.linkColumnHelp":
    "A code that identifies each sub-basin (the same one used when the project was created) or, if there's no such column, a name that matches the sub-basin's description.",
  "gisLayerPanel.linkedCount": "{n} of {total} sub-basins linked with this column.",
  "gisLayerPanel.linkLayer": "Link layer",
  "gisLayerPanel.allAlreadyPresent":
    "All the geometries in this file are already in the project — there's nothing to recover.",
  "gisLayerPanel.missingCount":
    "{n} of {total} geometries aren't already in the project (compared by geometry, not by name) — probably deleted sub-basins. They're recovered as new, without the configuration they had before (zone, topology, settings...).",
  "gisLayerPanel.subBasinName": "Sub-basin name",
  "gisLayerPanel.recoverOne": "Recover {n} sub-basin",
  "gisLayerPanel.recoverMultiple": "Recover {n} sub-basins",

  // --- LinkGisPanel.tsx ---
  "linkGisPanel.linkMode": "Link",
  "linkGisPanel.createMode": "Create new",
  "linkGisPanel.uploadShapefileOf": "Upload the {plural} shapefile or a GeoJSON.",
  "linkGisPanel.linkColumnHelp":
    "A code that identifies each {singular} (the same one used when the project was created) or, if there's no such column, a name that matches the real name.",
  "linkGisPanel.linkedCount": "{n} of {total} {plural} linked with this column.",
  "linkGisPanel.allAlreadyLinked":
    "All the geometries in this file are already linked to some {singular} in the project — there's nothing new to create.",
  "linkGisPanel.missingCount":
    "{n} of {total} geometries aren't linked to any {singular} in the project (compared by geometry, not by name) — they can be created as new {plural}.",
  "linkGisPanel.nameOf": "{singular} name",
  "linkGisPanel.createButton": "Create {n} {name}",

  // --- AquifersPanel.tsx ---
  "common.aquiferPlural": "aquifers",
  "aquifersPanel.topologyTitle": "Discharge aquifer: which aquifer each sub-basin drains into",
  "aquifersPanel.assignAquifer": "Assign aquifer",
  "aquifersPanel.catalog": "All aquifers",

  // --- OutletPointsPanel.tsx ---
  "common.outletPointPlural": "outlet points",
  "outletPointsPanel.topologyTitle":
    "Surface discharge: which outlet point each sub-basin drains into",
  "outletPointsPanel.assignOutlet": "Assign outlet",
  "outletPointsPanel.catalog": "All outlet points",

  // --- SeriesPanel.tsx ---
  "seriesPanel.formatHelpSubBasin":
    'CSV with ";" separator, {format} dates, and one column per sub-basin — the column name must match the sub-basin\'s name. Careful: this replaces the ENTIRE series for ALL sub-basins, not just adds or updates columns — sub-basins missing from the file end up with no data.',
  "seriesPanel.formatHelpAquifer":
    'CSV with ";" separator, {format} dates, and one column per aquifer — the column name must match the aquifer\'s name. Careful: this replaces the ENTIRE series for ALL aquifers, not just adds or updates columns — aquifers missing from the file end up with no data.',
  "seriesPanel.mapSelectionHelpSubBasin":
    "This chart shows the sub-basin selected on the map or in the card view (the first with data, until you select another).",
  "seriesPanel.mapSelectionHelpPumping":
    "This chart shows the aquifer or sub-basin selected on the map or in the card view (the first with data, until you select another) — pumping can be linked to either.",

  // --- SeriesChart.tsx ---
  "seriesPanel.noSeriesYet": "This series hasn't been loaded yet.",
  "seriesPanel.showing": "Showing: {name}",
  "seriesPanel.loading": "Loading…",

  "seriesPanel.fetchFromAemet": "Get series from AEMET",
  "seriesPanel.gapWarningOne":
    "1 sub-basin has a gap in its precipitation or ETP — likely left over from an earlier range extend that didn't finish.",
  "seriesPanel.gapWarningMultiple":
    "{n} sub-basins have gaps in their precipitation or ETP — likely left over from an earlier range extend that didn't finish.",
  "seriesPanel.gapWarningComplete": "Complete these {n}",
  "seriesPanel.gapWarningCompleting": "Completing {done}/{total}…",

  // --- NameDrawingDialog.tsx ---
  "nameDrawingDialog.new": "New {tool}",

  // --- FieldsPreviewTable.tsx ---
  "fieldsPreviewTable.showingFirst": "Showing the first 8 of {n} geometries.",

  // --- DrawButton.tsx ---
  "drawButton.draw": "Draw {tool}",
  "drawButton.cancelDrawing": "Cancel drawing",

  // --- EditSubBasinDialog.tsx ---
  "editSubBasinDialog.title": 'Edit sub-basin "{name}"',
  "editSubBasinDialog.basicData": "Basic data",
  "editSubBasinDialog.precipitationAdjustment": "Precipitation adjustment",
  "editSubBasinDialog.etpAdjustment": "ETP adjustment",
  "editSubBasinDialog.temperatureAdjustment": "Temperature adjustment",
  "editSubBasinDialog.fetchFromAemet": "Fetch from AEMET",
  "editSubBasinDialog.aemetNoGeometry":
    "This sub-basin has no linked geometry — the nearest weather station can't be found.",
  "editSubBasinDialog.aemetSource":
    "Data from AEMET: station {name} ({km} km away), fetched on {date}.",
  "editSubBasinDialog.aemetSourceRocio":
    "Data from AEMET's ROCIO grid (updated by AEMET on {aemetDate}): {n} cells inside the sub-basin. Fetched on {date}.",
  "editSubBasinDialog.aemetSourceRocioNoDate":
    "Data from AEMET's ROCIO grid: {n} cells inside the sub-basin. Fetched on {date}.",
  "editSubBasinDialog.viewAemetReport": "View AEMET report",
  "editSubBasinDialog.aemetReportTitle": "AEMET report",

  // --- aemet.ts / rocio.ts (error codes from functions/api/ -- the server has no i18n access, see aemetProtocol.ts) ---
  "aemetFetch.notConfigured": "This deployment doesn't have the AEMET key configured.",
  "aemetFetch.rateLimited":
    "Too many requests to AEMET from this connection — wait a moment before retrying.",
  "aemetFetch.upstreamError": "AEMET responded with an error ({status}).",
  "aemetFetch.unexpectedResponse": "Unexpected response from AEMET.",
  "aemetFetch.notFound": "The requested data isn't available on this deployment yet.",

  // --- AemetFetchDialog.tsx ---
  "aemetDialog.title": "Fetch series from AEMET",
  "aemetDialog.legalNotice":
    "The climate data shown comes from Spain's State Meteorological Agency (AEMET) — its reuse is authorized citing AEMET as the author of the information.",
  "aemetDialog.viewLegalNotice": "See legal notice ↗",
  "aemetDialog.pending": "Queued — will start once it's its turn.",
  "aemetDialog.searchingStations": "Looking for nearby stations…",
  "aemetDialog.searchingCells": "Looking for ROCIO grid cells…",
  "aemetDialog.stationsError": "Could not fetch AEMET's station list: {error}",
  "aemetDialog.downloadError": "Could not complete the download: {error}",
  "aemetDialog.noStations": "No AEMET station was found within 100 km.",
  "aemetDialog.noRocioCoverage":
    "This sub-basin falls outside the ROCIO grid's coverage (mainland Spain and the Balearic Islands).",
  "aemetDialog.mainStation": "Main station: {name} ({km} km away, {elevation} m altitude).",
  "aemetDialog.backupStations": "Backup stations, in case some days are missing: {list}.",
  "aemetDialog.rocioCellsOne": "1 cell from the ROCIO grid inside this sub-basin will be used.",
  "aemetDialog.rocioCellsMany": "{n} cells from the ROCIO grid inside this sub-basin will be used.",
  "aemetDialog.rangeTitle": "Date range",
  "aemetDialog.rocioRangeLimit":
    "The ROCIO grid only has data from 1951-01-01 to 2022-12-31 — days outside that range will be left without data.",
  "aemetDialog.rangeFixedNotice":
    "A precipitation series is already loaded, from {from} to {to} — that exact range will be requested from AEMET so it matches.",
  "aemetDialog.startDate": "Start date",
  "aemetDialog.endDate": "End date",
  "aemetDialog.invalidRange": "The end date must be after the start date.",
  "aemetDialog.methodTitle": "Method",
  "aemetDialog.methodNearest": "Nearest station (falls back to the next one)",
  "aemetDialog.methodIdw": "Distance-weighted average (IDW)",
  "aemetDialog.methodNearestHelp":
    "Each day uses the nearest station with a value; if that one fails, falls back to the next nearest.",
  "aemetDialog.methodIdwHelp":
    "Each day blends EVERY candidate station that has a value, weighted by the inverse square of its distance (closer stations count more) — tends to do better than the nearest station alone when it's somewhat far or the terrain is complex.",
  "aemetDialog.methodRocio": "ROCIO grid (AEMET, 5 km)",
  "aemetDialog.methodRocioHelp":
    "Daily precipitation and temperature from AEMET's interpolated grid (ROCIO_IBEB, ~5 km, 1951 onward).",
  "aemetDialog.estimateOneMinute": "It'll take about 1 minute.",
  "aemetDialog.estimateMinutes": "It'll take about {min} minutes.",
  "aemetDialog.startDownload": "Start download",
  "aemetDialog.downloadProgress": "Downloading from {from} to {to} — currently at {current}.",
  "aemetDialog.continuesInBackground":
    "You can close this window — the download keeps going in the background and applies itself once it's done. To see it again, check AEMET downloads in the top bar.",
  "aemetDialog.noData": "no data",
  "aemetDialog.sourceOneDay": "{source}: 1 day",
  "aemetDialog.sourceDays": "{source}: {n} days",
  "aemetDialog.reportTitle": "Before applying",
  "aemetDialog.failedWindows":
    "{n} periods could not be completed — those days will be left without data:",
  "aemetDialog.failedWindowRange": "{from} to {to}",
  "aemetDialog.retryFailedWindows": "Retry {n} failed periods",
  "aemetDialog.retrying": "Retrying… {done} of {total}.",
  "aemetDialog.appliedAutomatically":
    "Applied automatically once the download finished — this summary is saved, check it later with the report button.",
  "aemetDialog.retryApply": "Retry applying",
  "aemetDialog.subBasinDeleted":
    "The sub-basin was deleted before the download finished — there's nowhere left to apply the data.",

  // --- Pagination.tsx ---
  "pagination.first": "First page",
  "pagination.previous": "Previous page",
  "pagination.page": "Page",
  "pagination.of": "of {total}",
  "pagination.next": "Next page",
  "pagination.last": "Last page",

  // --- AemetBulkFetchDialog.tsx ---
  "aemetBulkDialog.title": "Get series from AEMET",
  "aemetBulkDialog.chooseMode": "What do you want to do?",
  "aemetBulkDialog.completeMode": "Complete missing sub-basins ({n})",
  "aemetBulkDialog.extendMode": "Extend the date range",
  "aemetBulkDialog.back": "← Back",
  "aemetBulkDialog.nothingToDo":
    "This project has no sub-basins with data pending, and none with AEMET data to extend.",
  "aemetBulkDialog.oneMissing": "1 sub-basin with data pending.",
  "aemetBulkDialog.multipleMissing": "{n} sub-basins with data pending.",
  "aemetBulkDialog.allComplete": "Every sub-basin already has its AEMET series.",
  "aemetBulkDialog.rangeHelp":
    "This project has no series loaded yet — pick the date range to request from AEMET, it will be used for every download below.",
  "aemetBulkDialog.downloadAll": "Download all ({n})",
  "aemetBulkDialog.downloadingAll": "Downloading... {done}/{total}",
  "aemetBulkDialog.download": "Download",
  "aemetBulkDialog.noCoverage": "Outside AEMET's coverage.",
  "aemetBulkDialog.retry": "Retry",
  "aemetBulkDialog.completed": "Done ✓",
  "aemetBulkDialog.completedWithGaps":
    "Done, {n} windows without data — check the sub-basin's report.",
  "aemetBulkDialog.currentRange": "Series' current range: {from} to {to}.",
  "aemetBulkDialog.newRangeTitle": "New range to add",
  "aemetBulkDialog.newRangeHelp":
    "The new range continues right where the current one ends (or right before it starts), with no calendar gap — only the other end needs picking.",
  "aemetBulkDialog.direction": "Direction",
  "aemetBulkDialog.directionForward": "Later years (bring it up to date)",
  "aemetBulkDialog.directionBackward": "Earlier years",
  "aemetBulkDialog.currentData": "Current data: {from} to {to}",
  "aemetBulkDialog.extend": "Extend",
  "aemetBulkDialog.extendAll": "Extend all ({n})",
  "aemetBulkDialog.extendingAll": "Extending... {done}/{total}",
  "aemetBulkDialog.extendingAllRange": "Downloading {from} to {to} for every sub-basin.",

  // --- AemetJobsPopover.tsx ---
  "aemetJobsPopover.title": "AEMET downloads",
  "aemetJobsPopover.phasePending": "Waiting to start",
  "aemetJobsPopover.phaseSearchingStations": "Looking for stations…",
  "aemetJobsPopover.phaseSearchingCells": "Looking for ROCIO cells…",
  "aemetJobsPopover.phaseNoStations": "No nearby stations",
  "aemetJobsPopover.phaseNoCoverage": "No ROCIO coverage",
  "aemetJobsPopover.phaseError": "Error",
  "aemetJobsPopover.phaseWaitingRange": "Waiting for a date range",
  "aemetJobsPopover.phaseApplied": "Applied",
  "aemetJobsPopover.cancel": "Cancel",
  "aemetJobsPopover.remove": "Remove from list",
  "aemetJobsPopover.clearFinished": "Clear finished",

  // --- NewProject.tsx ---
  "newProject.outletPoints": "Outlet Pts.",
  "newProject.review": "Review",
  "newProject.title": "Create new project",
  "newProject.projectName": "Project name",
  "newProject.wizardExplanation":
    'You can finish the wizard at any point with "Finish" — the project is created with whatever you\'ve set up so far, and anything left (more sub-basins, zones, series…) can be added later by editing the created project.',
  "newProject.errorCount": "{n} error",
  "newProject.errorsCount": "{n} errors",
  "newProject.warningCount": "{n} notice",
  "newProject.warningsCount": "{n} notices",

  // --- Step1SubBasins.tsx ---
  "step1.uploadShapefile": "Upload a shapefile or geojson of sub-basins.",
  "step1.geometriesFound": "{n} geometries found. Which column is which?",
  "step1.importButton": "Import {n} sub-basins",
  "step1.inputSummary": "{name} ({area} km²)",
  "step1.createButton": "Create {n} sub-basins",
  "step1.fromShapefile": "From shapefile",
  "step1.createManually": "Create by hand",
  "step1.csvTab": "CSV",
  "step1.noSubBasinsTab": "No sub-basins",
  "step1.subBasinsCreated": "You already have {n} sub-basins created.",
  "step1.editHelp":
    "You can rename any of them, change their area, delete one, or add another before continuing — none of this is lost if you go back to this step later.",
  "step1.addAnother": "Add another",
  "step1.uploadCsvButton": "Upload CSV",
  "step1.csvHelp": "One row per sub-basin, with a header: name,area.",
  "step1.csvImported": "{n} sub-basins imported from the CSV.",
  "step1.mitecoTab": "MITECO",
  "step1.mitecoHelp":
    "Click a basin on the map to automatically import all of its MITECO sub-basins.",
  "step1.mitecoSearchingIn": "Searching for sub-basins in {name}…",
  "step1.mitecoNoneFoundIn": "{name}: no MITECO sub-basin was found inside it.",
  "step1.mitecoSummary": "{name} — {n} sub-basins found.",
  "step1.mitecoManyFound":
    "That's a lot ({n}) — importing them and fetching their AEMET series can take several minutes.",
  "step1.mitecoRocioRangeReduced":
    'With this many sub-basins, only the last {years} years of the ROCIO grid will be requested (instead of the full record since 1951), to avoid overloading the browser — it can be extended later, sub-basin by sub-basin, from "Fetch from AEMET".',

  // --- Step2Zones.tsx ---
  "step2.chooseModel": "Choose a model for the zone.",
  "step2.replicateOutletsButton": "Replicate from outlet points",
  "step2.replicateOutletsHelp":
    "Creates one zone per outlet point, with the chosen model, and assigns it to the same sub-basins that outlet point already has.",
  "step2.replicateOutletsUpToDate":
    "Nothing to replicate — every outlet point already has its zone.",
  "step2.replicateOutletsChooseModel": "Choose a model before replicating.",
  "step2.zoneHelp":
    "Each zone has a model (rainfall-runoff or snow) and its default, editable parameters. Assigning a zone to a sub-basin gives that sub-basin the zone's model.",
  "step2.zoneNamePlaceholder": "Zone name",
  "common.modelPlaceholder": "— model —",
  "step2.snowSuffix": "(snow)",
  "step2.createZone": "Create zone",
  "step2.deleteZonePermanent": "Delete zone (permanent)",
  "step2.deleteZoneDialogTitle": "Delete zone",
  "step2.editZoneTitle": "Edit zone",
  "common.rename": "Rename",
  "common.model": "Model",
  "step2.changeModelWarning":
    "Changing the model replaces the current parameters and state variables with the new model's defaults.",
  "step2.parametersOf": 'Parameters for "{name}"',
  "step2.parameterScaleWarning":
    'Parameters with "1/time" or "time" units (Alfa, K0-K2, Kperc, X4...) are relative to the project\'s own time step: one day for a daily project, one month for a monthly one. The app never rescales these values automatically when the scale changes — they must already be entered calibrated for that step.',
  "common.parameter": "Parameter",
  "common.value": "Value",
  "common.units": "Units",
  "step2.stateVariablesOf": 'Initial state variables for "{name}"',
  "common.variable": "Variable",
  "common.initialValue": "Initial value",
  "step2.assignZoneTitle": "Assign zone to sub-basins",
  "step2.mapAssignHelp":
    "Click a sub-basin on the map to (de)select it, or hold Shift and drag to select several (Ctrl+drag to remove them).",
  "step2.mapOrCardsAssignHelp":
    "Click a sub-basin on the map or in the cards to (de)select it, or hold Shift and drag to select several (Ctrl+drag to remove them).",
  "step2.assignZoneButton": "Assign zone",
  "step2.assignSnowZoneTitle": "Assign snow zone to sub-basins",
  "step2.assignSnowZoneButton": "Assign snow zone",

  // --- BulkAssigner.tsx ---
  "bulkAssigner.currentlyAssigned": "Currently assigned",
  "bulkAssigner.buttonWithCount": "{text} ({n})",

  // --- Step3Outlets.tsx ---
  "step3.editOutletPointTitle": "Edit outlet point",
  "step3.editAquiferTitle": "Edit aquifer",
  "step3.observedSeries": "Observed series",
  "common.outletPointPlaceholder": "— outlet point —",
  "step3.deleteOutletPointPermanent": "Delete outlet point (permanent)",
  "step3.deleteOutletPointDialogTitle": "Delete outlet point",
  "step3.noOutletPointWarning":
    "Without an outlet point, this aquifer won't appear in the results.",
  "step3.noOutletPointSuffix": " (no outlet point)",
  "step3.deleteAquiferPermanent": "Delete aquifer (permanent)",
  "step3.deleteAquiferDialogTitle": "Delete aquifer",
  "step3.title": "Outlet points",
  // Also used by OutletPointsPanel.tsx's own "replicate" button, in the editor.
  "step3.replicateZonesButton": "Replicate from zones",
  "step3.replicateZonesHelp":
    "Creates one outlet point per zone and assigns it to the same sub-basins that zone already has.",
  "step3.replicateZonesUpToDate": "Nothing to replicate — every zone already has its outlet point.",

  // --- CalibrationPanel.tsx ---
  "calibration.title": "Parameter calibration",
  "calibration.outletPoint": "Outlet point",
  "calibration.noPointsWithObservedSeries":
    'No outlet point has an observed series yet. Pick one to open it and add one in its "Observed series" tab:',
  "calibration.noOutletPointsInProject": "This project doesn't have any outlet point yet.",
  "calibration.unitWarning":
    "The observed series must be in the same units as the project's aggregated result ({unit}).",
  "calibration.zone": "Zone to calibrate",
  "calibration.noZonesWithModel": "No zone with a model assigned drains into this outlet point.",
  "calibration.parameters": "Parameters to calibrate",
  "calibration.calibrationPeriodTitle": "Calibration period",
  "calibration.calibrationStart": "Start",
  "calibration.calibrationEnd": "End",
  "calibration.calibrationDatesOutOfOrder": "Start must be on or before end.",
  "calibration.validationPeriodTitle": "Validation period",
  "calibration.validationStart": "Start",
  "calibration.validationEnd": "End",
  "calibration.validationDatesOutOfOrder": "Start must be on or before end.",
  "calibration.insufficientCalibrationData":
    "The calibration period only has {n} valid day(s) with an observed value (recommended minimum: {min}).",
  "calibration.insufficientValidationData":
    "The validation period only has {n} valid day(s) with an observed value (recommended minimum: {min}).",
  "calibration.warmUpTitle": "Warm-up period",
  "calibration.warmUpHelp":
    "Simulates this period right before each window (calibration and validation) so the model's internal reservoirs (soil moisture, snow, aquifer...) reach a realistic state before scoring starts — those days are simulated but don't count toward the objective function.",
  "calibration.warmUpAmount": "Duration",
  "calibration.warmUpUnit.days": "Days",
  "calibration.warmUpUnit.weeks": "Weeks",
  "calibration.warmUpUnit.months": "Months",
  "calibration.warmUpUnit.years": "Years",
  "calibration.warmUpClampedCalibration":
    "There were only {n} day(s) of data before the calibration period — that shorter warm-up was used instead.",
  "calibration.warmUpClampedValidation":
    "There were only {n} day(s) of data before the validation period — that shorter warm-up was used instead.",
  "calibration.missingSeries":
    "Precipitation or ETP is missing from the project — can't calibrate.",
  // runCalibration.ts's own defensive checks — shouldn't be reachable in
  // the normal flow (the panel already filters to valid choices), but
  // translated like any other error instead of hard-coded English text.
  "calibration.dateOutsidePrecipitation":
    "Date {date} is outside the project's precipitation series.",
  "calibration.zoneNotFound": "Zone not found, or has no rainfall-runoff model assigned.",
  "calibration.outletPointNotFound": "Outlet point not found.",
  "calibration.noObservedSeriesForOutlet": "No observed series for this outlet point.",
  "calibration.unknownParameter": "Unknown parameter {code} for this zone's model.",
  "calibration.noCalibrationRangeForParameter": 'No calibration range for parameter "{name}".',
  "calibration.launch": "Calibrate",
  "calibration.calculating": "Calculating…",
  "calibration.evaluations": "{n} evaluations",
  "calibration.stopReason.maxEvaluations": "Finished: the evaluation limit was reached.",
  "calibration.stopReason.parameterSpaceConverged":
    "Finished: the population converged to a small parameter space.",
  "calibration.stopReason.objectiveConverged":
    "Finished: the objective function stopped improving significantly.",
  "calibration.applyParameters": "Apply these parameters",
  "calibration.discard": "Discard",
  "calibration.progressTitle": "Progress (best objective function value found)",
  "calibration.indicatorsTable.indicatorHeader": "Indicator",
  "calibration.indicatorsTable.initialHeader": "Initial",
  "calibration.indicatorsTable.finalHeader": "Final",
  "calibration.indicatorsTable.validationHeader": "Validation",
  "calibration.indicator.1": "Nash-Sutcliffe (NSE)",
  "calibration.indicator.2": "Log Nash-Sutcliffe",
  "calibration.indicator.3": "Pearson correlation",
  "calibration.indicator.4": "Symmetry",
  "calibration.indicator.5": "Percent bias (Pbias)",
  "calibration.indicator.6": "RSR",

  // --- Step4Topology.tsx ---
  "step4.surfaceOutlet": "Sub-basin assignment",
  "step4.outletAquifer": "Sub-basin assignment",

  // --- Step5Series.tsx ---
  "common.optional": "(optional)",
  "common.chooseFile": "Choose file",
  "common.chooseFiles": "Choose files",
  "seriesLoader.summary": "{rows} rows, {m} of {total} {plural} with data.",
  "seriesLoader.importFailed":
    "Nothing from this file was imported — it's unchanged from before. {error}",
  "step5.help":
    'CSV with ";" separator, dd/mm/yyyy dates, and one column per sub-basin/aquifer (the column name must match the element\'s name).',

  // --- HelpPanel.tsx ---
  "helpPanel.title": "What can you do?",
  "helpPanel.closeAria": "Close help",
  "helpPanel.searchPlaceholder": 'Search — e.g. "draw", "notices", "file format"...',
  "helpPanel.noResults": 'No results for "{query}".',

  // --- App.tsx ---
  "app.calculationError": "Couldn't calculate: {message}",
  "app.calculationExcludedWarning":
    "{n} sub-basin(s) have no valid precipitation/ETP data at all within the calculated range and were excluded from the result: {list}.",
  "app.calculationTruncatedWarning":
    "{n} sub-basin(s) were only calculated up to the first empty/invalid precipitation or ETP value: {list}.",
  "app.truncatedFromDate": "no data from {date}",
  "app.savedProjectNotFoundError": "That saved project wasn't found — it may have been deleted.",
  "app.autosaveError":
    "Couldn't save the latest changes: {message}. They're still in memory, but refreshing or closing the tab would lose them — retry or export a copy (⋮ → Export copy) before continuing.",
  "app.saveAsError":
    "Couldn't save the copy: {message}. The project you had open is still intact, but the new copy wasn't created.",
  "saveErrorBanner.retry": "Retry",
  "saveErrorBanner.retrying": "Retrying…",
  "app.noFileSystemAccessApiError":
    "This browser doesn't support the File System Access API (showDirectoryPicker). Try Chrome or Edge.",
  "app.defaultProjectName": "Project",

  // --- engine.ts ---
  "engine.missingParameter":
    'Missing parameter "{key}" for model "{model}" — the project\'s parameter catalog may be corrupt.',
  "engine.missingStateVariable":
    'Missing state variable "{key}" for model "{model}" — the project\'s state-variable catalog may be corrupt.',

  // --- subBasinState.ts (reasons a sub-basin can't be calculated) ---
  "subBasinState.noZone": "No zone assigned.",
  "subBasinState.zoneDoesNotExist": "The assigned zone no longer exists.",
  "subBasinState.snowZoneDoesNotExist": "The assigned snow zone no longer exists.",
  "subBasinState.noOutlet": "No outlet point assigned.",
  "subBasinState.outletDoesNotExist": "The assigned outlet point no longer exists.",
  "subBasinState.aquiferDoesNotExist": "The assigned aquifer no longer exists.",
  "subBasinState.noPrecipEtpColumn": "No precipitation/ETP data of its own.",
  "subBasinState.noTempColumn": "No temperature data: snow isn't simulated.",

  // --- validations.ts (validateProject) ---
  "validations.noModelWarning":
    "{n} sub-basin(s) without an assigned zone (or model) — they'll be left out of the calculation.",
  "validations.gr2mNotMonthlyWarning":
    "{n} sub-basin(s) using the GR2M model ({list}), but the project is daily-scale — GR2M is a monthly model, its results won't be correct with this data.",
  "validations.noOutletWarning":
    "{n} sub-basin(s) without an assigned surface outlet point (they'll be left out of the outlet-point aggregation, but are still calculated): {list}.",
  "validations.aquifersWithoutOutletPointBlocking":
    "{n} aquifer(s) in use without an assigned outlet point: {list}.",
  "validations.duplicateName": "Duplicate name between a sub-basin and an aquifer: {list}.",
  "validations.noSubBasinWithModel":
    "No sub-basin has a zone (model) assigned: there's nothing to simulate.",
  "validations.precipSeriesNotLoaded": 'The "Precipitation" series hasn\'t been loaded.',
  "validations.etpSeriesNotLoaded": 'The "ETP" series hasn\'t been loaded.',
  "validations.precipDateGapsBlocking":
    'The "Precipitation" series has date gaps: {n} day(s) missing — {list}.',
  "validations.etpDateGapsBlocking": 'The "ETP" series has date gaps: {n} day(s) missing — {list}.',
  "validations.temperatureDateGapsBlocking":
    'The "Temperature" series has date gaps: {n} day(s) missing — {list}.',
  "validations.pumpingDateGapsBlocking":
    'The "Pumping" series has date gaps: {n} day(s) missing — {list}.',
  "validations.noColumnsWarning":
    "{n} sub-basin(s) with an assigned zone don't have their own precipitation/ETP data and will be excluded from the calculation: {list}.",
  "validations.noTempWarning":
    "{n} sub-basin(s) with an assigned snow zone don't have temperature data: they'll be calculated with their normal rainfall-runoff model, without simulating snow: {list}.",

  // --- units.ts (chooseCustomUnits) ---
  "units.emptyNameError": "The unit name can't be empty.",
  "units.invalidCoefficientError": "The coefficient must be greater than 0.",

  // --- exportCsvProject.ts (informe.txt) ---
  "exportReport.title": "CSV export report",
  "exportReport.introWithFindings":
    "{n} issue(s) were detected in the project's data. The CSV files were still generated with all available data anyway — this is informational only, no file was skipped and no row was omitted because of this:",
  "exportReport.introNoFindings": "No issues were detected in the project's data.",
  "exportReport.aquifersWithoutOutlet":
    "{n} aquifer(s) with no outlet point assigned (empty Cod_Apo column in Ent_Acu.csv): {list}.",
  "exportReport.duplicateNames":
    "Repeated name among several {kind} elements (the name is used as the identifier in the CSVs): {list}.",

  // --- ehd.ts (fallback names when the .ehd is missing a real name) ---
  "ehd.subBasinFallbackName": "Sub-basin {n}",
  "ehd.zoneFallbackName": "Zone {n}",
  "ehd.snowZoneFallbackName": "Zone {n}-snow",

  // --- examples.ts ---
  "examples.zoneFallbackName": "Zone {model}",
  "examples.fileLoadError": 'Couldn\'t load "{url}" ({status}).',
  "examples.fileNotFoundError": 'The file "{name}" doesn\'t exist in this example.',
  "examples.folderNotFoundError": 'The folder "{name}" doesn\'t exist in this example.',

  // --- project.ts (openProject) ---
  "project.noEhdFoundError": "No .ehd file was found in the selected folder.",
  "project.ehdTooLarge": '"{name}" is over 100 MB — too large to open.',
  "project.scenarioFolderNotFoundWarning":
    'The scenario folder "{folder}" (referenced by the project) wasn\'t found inside the selected folder.',
  "project.noScenarioPathWarning":
    'The project has no "RUTA_ESCENARIO" registered; its input files can\'t be located.',
  "project.fileNotDeclaredWarning": 'The project doesn\'t declare the "{label}" file.',
  "project.requiredSeriesNotReadWarning": 'Couldn\'t read "{file}" ({label}).',
  "project.optionalSeriesNotReadWarning":
    "Couldn't read \"{file}\" ({label}). It's optional, continuing without it.",

  // --- importCsvProject.ts (openCsvProject) ---
  "importCsvProject.cuencasNotFoundError":
    'No .ehd file or "Ent_Cuencas.csv" was found in the selected folder.',
  "importCsvProject.noConfigWarning":
    '"Ent_Config.csv" wasn\'t found — using the default units and options.',
  "importCsvProject.noPrecipitationWarning":
    "\"Ent_Precip.csv\" wasn't found in the folder — the precipitation series wasn't loaded.",
  "importCsvProject.noEtpWarning":
    "\"Ent_Etp.csv\" wasn't found in the folder — the ETP series wasn't loaded.",

  // --- updateProjectFromCsv.ts ---
  "updateCsvProject.dialogTitle": "Update from CSVs",
  "updateCsvProject.subBasinsUpdated": "{n} sub-basin(s) updated",
  "updateCsvProject.zonesSummary": "{updated} zone(s) updated, {created} new",
  "updateCsvProject.aquifersSummary": "{updated} aquifer(s) updated, {created} new",
  "updateCsvProject.outletsSummary": "{created} new outlet point(s)",
  "updateCsvProject.configUpdated": "Units and aquifer options updated",
  "updateCsvProject.apply": "Apply changes",
  "updateCsvProject.duplicateZoneAcrossFiles":
    'Zone "{name}" appears in more than one parameter file — the last one found was used.',
  "updateCsvProject.subBasinNotFound":
    '"{name}" doesn\'t match any sub-basin in the open project — that row was ignored (this never creates new sub-basins).',
  "updateCsvProject.unresolvedReference":
    'Sub-basin "{subBasin}" references a {kind} named "{name}" that doesn\'t exist — left unassigned.',

  // --- projectEntities.ts (creating/editing sub-basins, zones, outlet points, aquifers) ---
  "projectEntities.emptySubBasinName": "The sub-basin name can't be empty.",
  "projectEntities.emptyCsv": "The CSV file is empty.",
  "projectEntities.csvColumnsNotFound":
    "Couldn't find the name and area columns — the first row must be the header (e.g. name;area).",
  "projectEntities.emptyZoneName": "The zone name can't be empty.",
  "projectEntities.zoneAlreadyExists":
    'A zone named "{name}" with that same model type already exists.',
  "projectEntities.zoneDoesNotExist": "That zone doesn't exist.",
  "projectEntities.emptyOutletPointName": "The outlet point name can't be empty.",
  "projectEntities.emptyAquiferName": "The aquifer name can't be empty.",
  "projectEntities.invalidVo": "Vo (the aquifer's initial volume) must be greater than 0.",
  "projectEntities.invalidAlfa": "Alfa (the discharge coefficient) must be greater than 0.",
  "projectEntities.defaultSubBasinName": "Sub-basin {n}",
  "projectEntities.defaultOutletPointName": "Outlet {n}",
  "projectEntities.defaultAquiferName": "Aquifer {n}",
  "projectEntities.hbvModelNotFound": "The HBV model was not found in the catalog.",

  // --- gisUpload.ts (loadGisFiles) ---
  "gisUpload.noGeometries": "The file has no geometries.",
  "gisUpload.missingShp": "Missing the .shp file (or upload a .geojson).",
  "gisUpload.shapefileNoGeometries": "The shapefile has no geometries.",
  "gisUpload.missingDbf":
    "Missing the .dbf: the shapefile's attributes won't be viewable or usable.",
  "gisUpload.missingPrj": "Missing the .prj: coordinates are assumed to already be in WGS84.",
  "gisUpload.fileTooLarge": '"{name}" is over 100 MB — too large to import.',

  // --- gisLayer.ts (parseGeoJSON) ---
  "gisLayer.invalidJson": "The file isn't valid JSON.",
  "gisLayer.notFeatureCollection": 'The file isn\'t a "FeatureCollection"-type GeoJSON.',

  // --- exportProject.ts (importProject) ---
  "exportProject.decompressError": '"{name}" couldn\'t be decompressed — is it a valid .ehdz file?',
  "exportProject.readInternalFormatError": '"{name}" couldn\'t be read (internal format damaged).',
  "exportProject.notExportedError": '"{name}" isn\'t an exported EvalHid copy.',
  "exportProject.newerVersionError":
    '"{name}" was exported with a newer version of the app — reload the page and try again.',
  "exportProject.corruptedDataError":
    '"{name}" doesn\'t have the content expected of an EvalHid project (damaged or incomplete file).',

  // --- dailySeries.ts (parsePastedSeries / insertColumnInSeries) ---
  "dailySeries.emptyDateFields":
    'Date with empty fields (day="{day}", month="{month}", year="{year}")',
  "dailySeries.noPastedData": "No data was pasted.",
  "dailySeries.lineInvalidFormat":
    'Line {n}: expected two tab-separated columns (date and value) — "{line}".',
  "dailySeries.lineInvalidValue": 'Line {n}: value "{value}" is not a number.',
  "dailySeries.datesDoNotMatch":
    'The pasted dates ({n} rows) don\'t match the already-loaded series ({total} rows, from {from} to {to}) — paste the same date range, or replace the whole series from "Input series".',

  // --- seriesScale.ts ---
  "seriesScale.daily": "Daily",
  "seriesScale.monthly": "Monthly",
  "seriesScale.dailyPasteFormat": "dd/mm/yyyy",
  "seriesScale.monthlyPasteFormat": "mm/yyyy",
  "seriesScale.monthlyFormatDescription":
    "mm/yyyy (dd/mm/yyyy with the day fixed at 01 also accepted)",
  "seriesScale.autoDetectSuffix":
    "The project's scale isn't set yet — it's detected automatically from this first file.",
  "seriesScale.dailyDateFormatMismatch":
    'Date "{date}" not recognized — this project is daily-scale, expected dd/mm/yyyy.',
  "seriesScale.monthlyDateFormatMismatch":
    'Date "{date}" not recognized — this project is monthly-scale, expected mm/yyyy.',
  "seriesScale.dateFormatUnrecognized": 'Date "{date}" not recognized.',
  "seriesScale.aemetDailyOnly":
    "Only available for daily-scale projects — AEMET/ROCIO don't offer monthly series.",

  // --- genericLayer.ts / GenericLayersPanel.tsx ---
  "genericLayer.emptyName": "The layer name can't be empty.",
  "genericLayer.unsupportedGeometry":
    "Couldn't recognize the file's geometry type (points, lines or polygons expected).",
  "genericLayersPanel.intro":
    "Upload a file (shapefile or GeoJSON) to show it as a reference layer on the map.",
  "genericLayersPanel.addButton": "Add layer",
  "genericLayersPanel.deleteDialogTitle": "Delete layer",
  "genericLayersPanel.deletePermanent": "Delete layer (permanent)",
  "genericLayersPanel.kind.point": "Point",
  "genericLayersPanel.kind.line": "Line",
  "genericLayersPanel.kind.polygon": "Polygon",
  "genericLayersPanel.deleteMessage":
    '"{name}" will be permanently deleted — this can\'t be undone. You can upload the same file again if you need it later.',
  "genericLayersPanel.color": "Color",
  "genericLayersPanel.radius": "Radius",
  "genericLayersPanel.weight": "Weight",
  "genericLayersPanel.fillOpacity": "Opacity",
};
